import { ChangeDetectionStrategy, Component, Signal, computed, effect, inject, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CsIconModule } from '@components/icon';
import { QuizChatMessage, QuizChatService } from '@services/quiz-chat-service';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { UserService } from '@services/user';
import { ConnectionService } from '@services/connection-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { AppConfig } from 'client/app/app.config';

/**
 * Card for a photo-upload quiz question. Reads the current question from the page-provided
 * QuizChatService and resolves everything through InstantCheckupService (no inputs). When the
 * pair's other photo is already uploaded, renders a two-card row (uploaded preview + current
 * dashed capture card, Figma 60508-60801); otherwise the single dashed illustration card.
 * The CTA lives in the page footer; navigation to the instant-checkup flow is owned by the page.
 */
@Component({
  selector: 'app-quiz-photo-upload',
  templateUrl: './quiz-photo-upload.html',
  standalone: true,
  imports: [NgTemplateOutlet, CsIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuizPhotoUploadComponent {
  private readonly quizService = inject(QuizChatService);
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly userService = inject(UserService);
  private readonly connectionService = inject(ConnectionService);
  private readonly commonUtilityHelper = inject(CommonUtilityHelper);
  private readonly appConfig = inject(AppConfig);
  readonly scanFrameImage: string = '/assets/images/complete-profile/scan-frame.svg';
  readonly dashedBorderImage: string = '/assets/images/quiz-photo-dashed-border.svg';
  readonly dashedBorderCardImage: string = '/assets/images/quiz-photo-dashed-border-card.svg';
  readonly checkIcon: string = 'circle_tick_filled';

  /** Tapping the dashed capture card runs the same instant-checkup action as the footer CTA. */
  readonly capture = output<void>();
  /** Tapping the already-uploaded preview opens that photo's instant-checkup detail (emits its id). */
  readonly openInstantCheckup = output<string>();

  private readonly alternatePhotoState = signal<string>('');
  // Instant-checkup id of the alternate photo (from the API path); '' when only a URL is known.
  private readonly alternateCheckupId = signal<string>('');
  // Canonical (non-signed) URL of the alternate photo, used to resolve the checkup id lazily on tap.
  private alternateCanonicalUrl = '';

  private readonly currentCheckupType = computed(() => {
    const questionId = this.quizService.currentQuestion()?.get('questionId');
    return questionId ? this.instantCheckupService.getPhotoCheckupType(questionId) : '';
  });
  private readonly alternateCheckupType = computed(
    () => this.instantCheckupService.getAlternatePhotoType(this.currentCheckupType()),
  );

  readonly imageUrl: Signal<string> = computed(() => {
    if (!this.quizService.photoCtaOption()) return '';
    const checkupType = this.currentCheckupType();
    if (!checkupType) return '';
    return this.instantCheckupService.getPhotoIllustrationUrl(checkupType, this.userService.getUserGender());
  });
  readonly currentLabel: Signal<string> = computed(() => this.instantCheckupService.getPhotoTypeLabel(this.currentCheckupType()));
  readonly alternateLabel: Signal<string> = computed(() => this.instantCheckupService.getPhotoTypeLabel(this.alternateCheckupType()));
  readonly alternatePhotoUrl: Signal<string> = this.alternatePhotoState.asReadonly();
  readonly isTwoCard: Signal<boolean> = computed(() => !!this.alternatePhotoState());

  constructor() {
    effect(() => {
      const alternateType = this.alternateCheckupType();
      const messages = this.quizService.messages();
      void this.resolveAlternatePhoto(alternateType, messages);
    });
  }

  /** Show the pair's other photo if the user already uploaded it — chat history first, API fallback. */
  private async resolveAlternatePhoto(alternateType: string, messages: QuizChatMessage[]): Promise<void> {
    this.clearAlternate();
    if (!alternateType) return;
    const alternateQuestionId = this.instantCheckupService.getPhotoQuestionId(alternateType);
    const uploaded = messages.find(
      (message) => message.get('Owner') === this.appConfig.Shared.Assistant.Owner.USER
        && message.get('questionId') === alternateQuestionId,
    );
    // `Message` holds the canonical (non-loadable) GCS path; the signed, display-ready URL is on
    // the temp* fields — same fallback chain the chatV2 image-card uses.
    const fromChat = uploaded && (uploaded.get('tempCompressedImage') || uploaded.get('tempThumbnailUrl')
      || uploaded.get('tempUrl') || uploaded.get('Message'));
    if (await this.isDisplayableImage(fromChat)) {
      this.alternatePhotoState.set(fromChat);
      // No checkup id in the chat row — keep the canonical URL to resolve it on tap.
      this.alternateCanonicalUrl = (uploaded.get('Message') as string) || fromChat;
      return;
    }
    const checkup = await this.instantCheckupService.getRecentInstantCheckupOfType(
      this.connectionService.getActingUser(), alternateType,
    );
    const apiUrl = checkup?.imagePath || '';
    // A deleted photo (URL present but the file is 404) collapses back to the single dashed card.
    if (await this.isDisplayableImage(apiUrl)) {
      this.alternatePhotoState.set(apiUrl);
      this.alternateCheckupId.set(checkup?.objectId || '');
      this.alternateCanonicalUrl = apiUrl;
    }
  }

  private clearAlternate(): void {
    this.alternatePhotoState.set('');
    this.alternateCheckupId.set('');
    this.alternateCanonicalUrl = '';
  }

  private async isDisplayableImage(url: string): Promise<boolean> {
    if (!url) return false;
    return !(await this.commonUtilityHelper.checkImageDeleted(url));
  }

  onCapture(): void {
    this.capture.emit();
  }

  /** Keyboard activation for the div-as-button capture card (Enter/Space). */
  onCaptureKeydown(event: Event): void {
    event.preventDefault();
    this.onCapture();
  }

  /** Open the uploaded preview's instant-checkup detail — resolve the id from the URL if unknown. */
  async onOpenAlternate(): Promise<void> {
    let checkupId = this.alternateCheckupId();
    if (!checkupId && this.alternateCanonicalUrl) {
      const checkup = await this.connectionService.fetchInstantCheckupByImageUrl(
        this.connectionService.getActingUser(), this.alternateCanonicalUrl,
      );
      checkupId = checkup?.id || '';
    }
    if (checkupId) this.openInstantCheckup.emit(checkupId);
  }

  onOpenAlternateKeydown(event: Event): void {
    event.preventDefault();
    void this.onOpenAlternate();
  }
}
